import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Space, message, Typography } from "antd";
import ListTable from "common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { getData } from "../../../apiServices/common";
import {
  QC_VALIDATION_EXPORT_REPORT_ENDPOINT,
  QC_VALIDATION_LIST_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import moment from "moment";

const QcValidationReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const { Title } = Typography;

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getQcValidationList = useCallback(
    async (filterValues = {}) => {
      setLoading(true);

      const isFilterUpdate =
        "start_date" in filterValues ||
        "end_date" in filterValues ||
        "line_id" in filterValues;

      const isClear = Object.keys(filterValues).length === 0;

      let filters = activeFilters;
      if (isClear) {
        filters = {};
        setActiveFilters({});
        setCurrentPage(1);
      } else if (isFilterUpdate) {
        filters = Object.fromEntries(
          Object.entries({
            date_from: filterValues.start_date
              ? moment(filterValues.start_date).format("YYYY-MM-DD")
              : null,
            date_to: filterValues.end_date
              ? moment(filterValues.end_date).format("YYYY-MM-DD")
              : null,
            line_id: filterValues.line_id || null,
          }).filter(([, value]) => value != null),
        );
        setActiveFilters(filters);
        setCurrentPage(1);
      }

      const bodyData = {
        page: filterValues.page || (isFilterUpdate || isClear ? 1 : currentPage),
        per_page: filterValues.per_page || 10,
        ...filters,
      };

      const response = await getData(QC_VALIDATION_LIST_ENDPOINT, false, bodyData);

      if (response?.data?.code === 200) {
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data || []);
      } else {
        message.error(
          response?.data?.message || "Error getting QC Validation Report data",
        );
      }

      setLoading(false);
    },
    [activeFilters, currentPage],
  );

  useEffect(() => {
    getQcValidationList({ page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    getQcValidationList({ page: newPage });
  };

  const handleViewDetails = (record) => {
    navigate(`/digitalization/qc-validation-report/view/${record?.id}`);
  };

  const handleExportExcel = async () => {
    if (!activeFilters?.date_from || !activeFilters?.date_to) {
      message.warning("Please select Start Date and End Date before downloading.");
      return;
    }

    setExportLoading(true);

    const response = await getData(
      QC_VALIDATION_EXPORT_REPORT_ENDPOINT,
      false,
      activeFilters,
      { "Content-Type": "blob" },
      "arraybuffer",
    );

    if (response?.data) {
      const outputFilename = `qc-validation-report_${activeFilters.date_from}_to_${activeFilters.date_to}.xlsx`;
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success("QC Validation Report downloaded successfully");
    } else {
      message.error("Failed to download QC Validation Report");
    }

    setExportLoading(false);
  };

  const tableColumns = [
    {
      title: "Validation Date",
      dataIndex: "validation_date",
      key: "validation_date",
      render: (value) => (value ? cglFormatDate(value) : "N/A"),
    },
    {
      title: "Line",
      dataIndex: "line_no",
      key: "line_no",
      render: (value) => value || "N/A",
    },
    {
      title: "QC Name",
      dataIndex: "qc_name",
      key: "qc_name",
      render: (value) => value || "N/A",
    },
    {
      title: "Q.I Name",
      dataIndex: "qi_name",
      key: "qi_name",
      render: (value) => value || "N/A",
    },
    {
      title: "Q.I ID Card No",
      dataIndex: "qi_id_card_no",
      key: "qi_id_card_no",
      render: (value) => value || "N/A",
    },
    {
      title: "Check Points",
      dataIndex: "check_points",
      key: "check_points",
      render: (checkPoints) =>
        checkPoints?.map((item) => item?.name).filter(Boolean).join(", ") ||
        "N/A",
    },
    {
      title: "Checked PCs",
      dataIndex: "checked_pcs",
      key: "checked_pcs",
      align: "right",
    },
    {
      title: "Total Defect Qty",
      dataIndex: "total_defect_qty",
      key: "total_defect_qty",
      align: "right",
    },
    {
      title: "Defect Rate (%)",
      dataIndex: "defect_rate",
      key: "defect_rate",
      align: "right",
      render: (value) => (value != null ? `${value}%` : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) => (
        <Typography.Link onClick={() => handleViewDetails(record)}>
          View
        </Typography.Link>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getQcValidationList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="qc_validation_report"
      />
    ),
    [showFilter],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={handleExportExcel}
              >
                Download Excel
              </Button>
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            </Space>
          }
          title={<Title level={5}>QC Validation Report</Title>}
        />
      }
      content={
        <>
          {memoizedCustomFilter}

          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              dataSource={tableData}
              columns={tableColumns}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                showSizeChanger: false,
                defaultPageSize: metaData?.defaultPageSize,
                current: metaData?.current,
                onChange: (newPage) => {
                  changePage(newPage);
                },
                defaultCurrent: metaData?.defaultCurrent,
                total: metaData?.total,
              }}
            />
          </div>
        </>
      }
    />
  );
};

export default QcValidationReport;
